<template>
  <div class="app-main">
    <transition name="fade">
      <component
        :is="layout"
        v-if="fetched">
        <transition name="switch">
          <router-view :key="$route.fullPath" />
        </transition>
      </component>
    </transition>
    <products-search-popup
      v-if="productsSearchPopupIsOpen"
      :currency-id="-1"
      :products-discount="0"
      :selected-currency-symbol="'PLN'"
      :product-index="0"
      :selected-tag-category="null"
      :selected-tag-category-value="null"
      :tag-categories="tagCategories"
      :hide-buttons="true" />

    <container-search-popup
      v-if="containerPreviewPopupIsOpen"
      :productID="containerPreviewProductID"
      :productCode="containerPreviewProductCode"
      :previewMode="true" />

    <d-modal
      v-if="displayPreviewPopup"
      id="hpl-image-preview-popup"
      @close="closePreviewPopup"
      size="lg">
      <d-modal-body class="p-0">
        <d-alert
          :show="!previewLoaded"
          theme="light"
          class="mt-3 text-center">
          {{ $t('ui.dataLoading') }}
        </d-alert>
        <img
          v-show="previewLoaded"
          :src="previewURL"
          @load="previewLoaded = true;"
          style="width: 100%; height: auto;" />
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="mx-auto"
          theme="danger"
          @click.prevent="closePreviewPopup">
          {{ $t('ui.close') }}
        </d-button>
      </d-modal-footer>
    </d-modal>
  </div>
</template>

<script>
import FormUtils from './utils/FormUtils.js';
import SanitizeUtils from './utils/SanitizeUtils.js';
import ProductsSearchPopup from './components/orders/ProductsSearchPopup.vue';
import ContainerSearchPopup from './components/orders/ContainerSearchPopup.vue';

export default {
  name: 'App',
  components: {
    'products-search-popup': ProductsSearchPopup,
    'container-search-popup': ContainerSearchPopup
  },
  data () {
    return {
      fetched: false,
      user: {},
      loadUserError: false,
      loadedTagCategories: false,
      tagCategories: [],
      productsSearchPopupIsOpen: false,
      containerPreviewPopupIsOpen: false,
      containerPreviewProductID: 0,
      containerPreviewProductCode: '',
      displayPreviewPopup: false,
      previewURL: null,
      previewLoaded: false,
      sysMessagedInterval: null,
      originalFaviconUrl: null
    }
  },
  computed: {
    layout () {
      if (!this.$route.meta) {
        return 'default-layout';
      }

      return `${this.$route.meta.layout || 'default'}-layout`;
    },
    currentUserId () {
      return this.$store.getters.getUserID;
    },
    unreadCount () {
      return this.$store.getters.getUnreadSystemMessagesCount;
    }
  },
  watch: {
    currentUserId (newId, oldId) {
      if (newId && newId !== oldId) {
        this.startSysMessagesPolling();
      } else if (!newId) {
        this.stopSysMessagesPolling();
      }
    },
    unreadCount: {
      handler (newCount) {
        this.updateVisualNotification(newCount);
      },
      immediate: true
    },
    $route () {
      this.$nextTick(() => {
        this.updateVisualNotification(this.unreadCount);
      });
    }
  },
  mounted () {
    let self = this;

    window.crmApp = {
      permissionsAvailable () {
        return self.$store.state && self.$store.state.user && self.$store.state.user.permissions;
      },
      hasPermissions (permissionName) {
        return self.$store.state.user.permissions && self.$store.state.user.permissions[permissionName];
      }
    };

    this.$bus.$on('sidebar-products-search-popup-show', this.openProductsSearchPopup);
    this.$bus.$on('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$on('container-preview-popup-open', this.openContainerPreviewPopup);
    this.$bus.$on('container-preview-popup-close', this.closeContainerPreviewPopup);
    this.$bus.$on('image-preview-popup-show', this.openPreviewPopup);
    this.$bus.$on('image-preview-popup-close', this.closePreviewPopup);

    if (!SanitizeUtils.getToken()) {
      this.fetched = true;
      return;
    }

    FormUtils.loadAdditionalData(this, {
      endpoint: '/users/item/me',
      outputKey: 'user',
      errorKey: 'loadUserError',
      successAction: (response) => {
        this.fetched = true;

        if (response && response.type === 'customer') {
          this.$router.push('/logowanie/invalid');
          return;
        }

        this.$store.commit('setUserID', response.id);
        this.$store.commit('setUserName', response.name);
        this.$store.commit('setUserEmail', response.email);
        this.$store.commit('setUserPermissions', response.permissions);
        this.$store.commit('setUserType', response.type);
        this.$store.commit('setUserLang', response.language_id);

        let currentLanguageID = this.$i18n.locale === 'pl' ? 1 : 2;

        if (response.language_id > 0 && currentLanguageID !== response.language_id) {
          window.localStorage.setItem('headwear-crm-ui-language', response.language_id);
          window.location.reload();
        }

        FormUtils.loadAdditionalData(this, {
          endpoint: '/tags/categories/items',
          loadedKey: 'loadedTagCategories',
          outputKey: 'tagCategories',
          errorKey: 'loadError',
          params: {
            with: ['tags']
          }
        });

        this.startSysMessagesPolling();
      }
    });
  },
  methods: {
    openProductsSearchPopup () {
      this.productsSearchPopupIsOpen = true;
    },
    closeProductsSearchPopup () {
      this.productsSearchPopupIsOpen = false;
    },
    openContainerPreviewPopup (productID, productCode) {
      this.containerPreviewProductID = productID;
      this.containerPreviewProductCode = productCode || '';
      this.containerPreviewPopupIsOpen = true;
    },
    closeContainerPreviewPopup () {
      this.containerPreviewPopupIsOpen = false;
      this.containerPreviewProductID = 0;
      this.containerPreviewProductCode = '';
    },
    openPreviewPopup (url) {
      this.displayPreviewPopup = true;
      this.previewLoaded = false;
      this.previewURL = url;
    },
    closePreviewPopup () {
      this.displayPreviewPopup = false;
      this.previewURL = null;
      this.previewLoaded = false;
    },
    startSysMessagesPolling () {
      this.stopSysMessagesPolling();

      if (!this.currentUserId) {
        return;
      }

      this.fetchSysNotifications();
      this.sysMessagedInterval = setInterval(this.fetchSysNotifications, 180000);

      this.$bus.$on('system-messages-refresh-trigger', this.fetchSysNotifications);
    },
    stopSysMessagesPolling () {
      if (this.sysMessagedInterval) {
        clearInterval(this.sysMessagedInterval);
        this.sysMessagedInterval = null;
      }
      this.$bus.$off('system-messages-refresh-trigger', this.fetchSysNotifications);
    },
    fetchSysNotifications () {
      if (!this.currentUserId) {
        return;
      }

      this.$http.get('/messages/items-for-user', { params: { 'return': 'count' } })
        .then(response => {
          const count = response.data.data.messages || 0;
          this.$store.commit('setUnreadSystemMessagesCount', count);
          this.updateVisualNotification(count);
        })
        .catch(error => {
          console.error('Błąd pobierania liczby nieprzeczytanych komunikatów:', error);
        });
    },
    updateVisualNotification (count) {
      const faviconLink = document.querySelector("link[rel*='icon']");

      if (faviconLink) {
        this.generateBadgeFavicon(count);
        this.updateDocumentTitle(0);
      } else {
        this.updateDocumentTitle(count);
      }

      this.updateNativeAppBadge(count);
    },
    updateDocumentTitle (count) {
      let currentTitle = document.title;

      const cleanTitle = currentTitle.replace(/^\([^)]+\)\s*/, '').trim();

      if (count > 0) {
        let displayCount = count > 99 ? '99+' : count;
        document.title = `(${displayCount}) ${cleanTitle}`;
      } else {
        document.title = cleanTitle;
      }
    },
    async generateBadgeFavicon (count) {
      const faviconLink = document.querySelector("link[rel*='icon']");
      if (!faviconLink) {
        return;
      }

      if (!this.originalFaviconUrl) {
        this.originalFaviconUrl = faviconLink.href;
      }

      const safeCount = parseInt(count, 10);

      if (safeCount <= 0) {
        faviconLink.href = this.originalFaviconUrl;
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = this.originalFaviconUrl;

      await new Promise((resolve) => {
        img.onload = resolve;
      });

      ctx.drawImage(img, 0, 0, 32, 32);

      let displayCount = safeCount > 99 ? '99+' : safeCount.toString();

      ctx.font = 'bold 15px Arial, sans-serif';
      const textMetrics = ctx.measureText(displayCount);
      const textWidth = textMetrics.width;

      const paddingX = 4;
      const badgeHeight = 18;
      const badgeWidth = Math.max(badgeHeight, textWidth + (paddingX * 2));

      const badgeX = 32 - badgeWidth;
      const badgeY = 32 - badgeHeight;
      const borderRadius = 5;

      ctx.beginPath();
      ctx.moveTo(badgeX + borderRadius, badgeY);
      ctx.lineTo((badgeX + badgeWidth) - borderRadius, badgeY);
      ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + borderRadius);
      ctx.lineTo(badgeX + badgeWidth, (badgeY + badgeHeight) - borderRadius);
      ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY + badgeHeight, (badgeX + badgeWidth) - borderRadius, badgeY + badgeHeight);
      ctx.lineTo(badgeX + borderRadius, badgeY + badgeHeight);
      ctx.quadraticCurveTo(badgeX, badgeY + badgeHeight, badgeX, (badgeY + badgeHeight) - borderRadius);
      ctx.lineTo(badgeX, badgeY + borderRadius);
      ctx.quadraticCurveTo(badgeX, badgeY, badgeX + borderRadius, badgeY);
      ctx.closePath();

      ctx.fillStyle = '#E53935';
      ctx.fill();

      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const textX = badgeX + (badgeWidth / 2);
      const textY = badgeY + (badgeHeight / 2) + 1;

      ctx.fillText(displayCount, textX, textY);

      faviconLink.href = canvas.toDataURL('image/png');
    },
    updateNativeAppBadge (count) {
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          navigator.setAppBadge(count).catch((error) => {
            console.error('Błąd natywnej plakietki:', error);
          });
        } else {
          navigator.clearAppBadge().catch((error) => {
            console.error('Błąd czyszczenia natywnej plakietki:', error);
          });
        }
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('sidebar-products-search-popup-show', this.openProductsSearchPopup);
    this.$bus.$off('products-search-popup-close', this.closeProductsSearchPopup);
    this.$bus.$off('container-preview-popup-open', this.openContainerPreviewPopup);
    this.$bus.$off('container-preview-popup-close', this.closeContainerPreviewPopup);
    this.$bus.$off('image-preview-popup-show', this.openPreviewPopup);
    this.$bus.$off('image-preview-popup-close', this.closePreviewPopup);

    this.stopSysMessagesPolling();
  }
};
</script>

<style lang="scss">
@import './assets/scss/custom-elements.scss';

.switch-enter-to,
.switch-leave-to {
  transition: opacity .25s ease-out;
}

.switch-enter,
.switch-leave-to {
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transform: none;
  width: 100%;
}

#hpl-image-preview-popup .modal-content {
  max-height: 90vh;

  .modal-body {
    height: 85vh;
    position: relative;

    img {
      display: block;
      left: 50%;
      object-fit: contain;
      max-height: 100%;
      max-width: 100%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
    }
  }
}
</style>
